// @flow
import React, {PureComponent, Component} from 'react';
import {DateRangePicker} from '@homeaway/react-date-pickers';
import withLingua from '../withLingua/withLingua';
import DayAvailability from '../DayAvailability/DayAvailability';
import {media} from '../../utils/breakpoints';
import classNames from 'classnames';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {injectIntl, FormattedMessage} from 'react-intl';
import PickerInputLabel from '../PickerInputLabel/PickerInputLabel';
import './ConnectedDateRangePicker.less';

import type {IntlShape} from 'react-intl';
import type {LinguaProps} from '../withLingua/withLingua';
import type {State as ReduxState} from '../../reducers';
import type {Element} from 'react';

type MessageDescriptor = {messageId: string, defaultText: string};

type OwnProps = {
    calendarIcon?: Element<typeof Component>,
    dateTextClass: string,
    endDate: string,
    hasDatesDescriptor?: MessageDescriptor,
    intl: IntlShape,
    isOpen: boolean,
    noDatesDescriptor?: MessageDescriptor,
    startDate: string
};

type StateProps = {
    isMobile: boolean,
    isTouchDevice: boolean,
    onDateRangeClear: () => void,
    onOpen: () => void
};

type Props = OwnProps & StateProps & LinguaProps;

type NumericDateOptionsType = 'numeric' | '2-digit';

type LiteralDateOptionsType = 'short' | 'long' | 'narrow';

type DateFormatOptions = NumericDateOptionsType | LiteralDateOptionsType;

type State = {
    modalTitleDescriptor: ?Object,
    edited: boolean
};

export type DateFormatOptionsType = {
    day?: NumericDateOptionsType,
    month?: DateFormatOptions,
    year?: NumericDateOptionsType
};

export const dateFormatOptionsMobile: DateFormatOptionsType = {
    day: 'numeric',
    month: 'short'
};

export const dateFormatOptions: DateFormatOptionsType = {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
};

const selectCheckInDescriptor = {
    messageId: 'selectCheckIn',
    defaultText: 'Select a check-in date'
};

const selectCheckOutDescriptor = {
    messageId: 'selectCheckOut',
    defaultText: 'Select a checkout date'
};

const createCalendarLabels = (messages) => ({
    monthNames: [
        messages['month.january'],
        messages['month.february'],
        messages['month.march'],
        messages['month.april'],
        messages['month.may'],
        messages['month.june'],
        messages['month.july'],
        messages['month.august'],
        messages['month.september'],
        messages['month.october'],
        messages['month.november'],
        messages['month.december']
    ],
    shortMonthNames: [
        messages['month.january.abbr'],
        messages['month.february.abbr'],
        messages['month.march.abbr'],
        messages['month.april.abbr'],
        messages['month.may.abbr'],
        messages['month.june.abbr'],
        messages['month.july.abbr'],
        messages['month.august.abbr'],
        messages['month.september.abbr'],
        messages['month.october.abbr'],
        messages['month.november.abbr'],
        messages['month.december.abbr']
    ],
    dayNames: [
        messages['day.sunday.threechar'],
        messages['day.monday.threechar'],
        messages['day.tuesday.threechar'],
        messages['day.wednesday.threechar'],
        messages['day.thursday.threechar'],
        messages['day.friday.threechar'],
        messages['day.saturday.threechar']
    ],
    shortDayNames: [
        messages['day.sunday'],
        messages['day.monday'],
        messages['day.tuesday'],
        messages['day.wednesday'],
        messages['day.thursday'],
        messages['day.friday'],
        messages['day.saturday']
    ].map((dayName) => dayName && dayName.slice(0, 2))
});

export class ConnectedDateRangePicker extends PureComponent<Props, State> {
    static defaultProps = {
        noDatesDescriptor: {
            messageId: 'condensedSearchBarNoDates',
            defaultText: 'Enter dates'
        },
        hasDatesDescriptor: {
            messageId: 'datePickerInputLabel',
            defaultText: 'Dates'
        }
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            modalTitleDescriptor: null,
            edited: false
        };
    }

    clearButtonLabel = () =>
        this.props.intl.formatMessage({
            id: 'datePickerClearLabel',
            defaultMessage: 'Clear'
        });

    continueButtonLabel = () => {
        const {formatMessage} = this.props.intl;
        return formatMessage({
            id: 'datePickerContinueLabel',
            defaultMessage: 'Continue'
        });
    };

    onSelectionModeChanged = (selectingEndDate: boolean) => {
        if (selectingEndDate) {
            this.setState({
                modalTitleDescriptor: selectCheckOutDescriptor,
                edited: true
            });
        }
    };

    onDateRangePickerOpen = () => {
        this.setState({
            modalTitleDescriptor: selectCheckInDescriptor,
            edited: false
        });
        this.props.onOpen();
    };

    getModalTitle = () => {
        const {startDate, endDate} = this.props;

        if (this.state.edited && startDate && endDate) {
            return null;
        }

        return this.state.modalTitleDescriptor ? (
            <FormattedMessage
                id={this.state.modalTitleDescriptor.messageId}
                defaultMessage={this.state.modalTitleDescriptor.defaultText}
            />
        ) : null;
    };

    onDateRangePickerCleared = () => {
        this.setState({
            modalTitleDescriptor: selectCheckInDescriptor,
            edited: true
        });
        this.props.onDateRangeClear();
    };

    render() {
        let {
            calendarIcon,
            dateTextClass,
            hasDatesDescriptor,
            isLinguaLoading,
            isOpen,
            isTouchDevice,
            isMobile,
            locale,
            messages,
            noDatesDescriptor,
            ...rest
        } = this.props;

        const {startDate, endDate} = this.props;
        let calendarLabels;

        if (!isLinguaLoading) {
            calendarLabels = createCalendarLabels(messages);
        }

        const dateTextDescriptor =
            !!startDate && !!endDate ? hasDatesDescriptor : noDatesDescriptor;

        let dateTextDescriptorInput1 = !isMobile
            ? {
                  defaultText: 'Arrive',
                  messageId: 'arrive'
              }
            : dateTextDescriptor;
        let dateTextDescriptorInput2 = {
            defaultText: 'Depart',
            messageId: 'depart'
        };

        /* If we are in GT dates test with no arrival,
        the picker is open and we are on desktop */
        if (!startDate && isOpen && !isMobile) {
            dateTextDescriptorInput1 = {
                defaultText: 'MM/DD/YYYY',
                messageId: 'calendarDateFormat'
            };
        } else if (!isMobile && startDate) {
            dateTextDescriptorInput1 = null;
        }

        if (!isMobile && endDate) {
            dateTextDescriptorInput2 = null;
        }

        return (
            <DateRangePicker
                {...rest}
                className={classNames('DateRangePicker', {
                    'DateRangePicker--gt': !isMobile,
                    'DateRangePicker--dateless': !startDate || !endDate,
                    'DateRangePicker__arrow-right':
                        !!startDate && !endDate && !isMobile
                })}
                dateFormatOptions={
                    isMobile ? dateFormatOptionsMobile : dateFormatOptions
                }
                disableTextInput
                inputIcon1={calendarIcon}
                inputIcon2={calendarIcon}
                inputLabel1={
                    <PickerInputLabel
                        textClass={dateTextClass}
                        {...dateTextDescriptorInput1}
                    />
                }
                inputLabel2={
                    <PickerInputLabel
                        textClass={dateTextClass}
                        {...dateTextDescriptorInput2}
                    />
                }
                centered={false}
                clearButtonLabel={this.clearButtonLabel()}
                consolidateInputs={isMobile}
                continueButtonLabel={this.continueButtonLabel()}
                edapLabel="search"
                fadeOutDuration={0}
                inputName1="checkin"
                inputName2="checkout"
                isMobile={isMobile}
                calendarLabels={calendarLabels}
                showArrow={false}
                showContinueButton={
                    (!!startDate && !!endDate) || (!startDate && !endDate)
                }
                dayTemplateComponent={DayAvailability}
                modalTitle={isMobile ? this.getModalTitle() : null}
                locale={locale}
                isOpen={isOpen}
                onOpen={this.onDateRangePickerOpen}
                onSelectionModeChange={this.onSelectionModeChanged}
                onDateRangeClear={this.onDateRangePickerCleared}
            />
        );
    }
}

const mapStateToProps = ({viewport: {breakpoint}}: ReduxState) => {
    const isMobile = !media(breakpoint, {minWidth: 'SM_MIN'});
    return {
        isMobile,
        isTouchDevice: isMobile || !media(breakpoint, {minWidth: 'MD_MIN'})
    };
};

export default compose(
    injectIntl,
    withLingua({
        namespace: 'global-templates/traveler',
        category: 'common'
    }),
    connect(
        mapStateToProps,
        null
    )
)(ConnectedDateRangePicker);
